import { useState, type ReactNode } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

export interface TabDef {
  key: string;
  /** Usually words; the AI tab carries the pactline.ai wordmark instead. */
  label: ReactNode;
  icon?: ReactNode;
  /** A count shown beside the label, e.g. open comments; hidden when zero. */
  badge?: number;
  content: ReactNode;
}

interface TabsProps {
  tabs: TabDef[];
  defaultKey?: string;
  /** Controlled mode: the parent owns the active tab (e.g. to switch tabs
   *  from an action elsewhere on the page). */
  activeKey?: string;
  onChange?: (key: string) => void;
  /** Where the panel chrome lives; "plain" lets each tab bring its own cards. */
  panelStyle?: "card" | "plain";
  /** Names the strip for screen readers. Say what these tabs are sections of. */
  ariaLabel?: string;
}

// Slimmer than it was: the strip is a way in to the page, not the page's
// headline, and at min-h-14/text-base it competed with the obligation's own
// title directly above it.
const tabBase =
  "relative min-h-10 shrink-0 whitespace-nowrap rounded-lg px-3.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))] focus-visible:ring-offset-2";
const tabActive = "bg-[rgb(var(--accent-fill))] text-[rgb(var(--accent-ink))] shadow-sm";
const tabIdle =
  "text-[rgb(var(--text-secondary))] hover:bg-[rgb(var(--card))] hover:text-[rgb(var(--text-primary))]";

export function Tabs({
  tabs,
  defaultKey,
  activeKey: controlledKey,
  onChange,
  panelStyle = "card",
  ariaLabel,
}: TabsProps) {
  const { t } = useTranslation("common");
  const [innerKey, setInnerKey] = useState(defaultKey ?? tabs[0]?.key);
  const activeKey = controlledKey ?? innerKey;
  const setActiveKey = (key: string) => {
    setInnerKey(key);
    onChange?.(key);
  };
  const active = tabs.find((tab) => tab.key === activeKey) ?? tabs[0];

  return (
    <div className="flex flex-col gap-4">
      <div
        role="tablist"
        aria-label={ariaLabel ?? t("tabs.sections")}
        // One row that scrolls sideways when the tabs outgrow it — wrapping
        // left a lone tab stranded on a second line under the rest.
        className="flex w-full gap-1 overflow-x-auto rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card-muted))] p-1 shadow-sm"
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={tab.key === activeKey}
            aria-controls={`tab-panel-${tab.key}`}
            onClick={() => setActiveKey(tab.key)}
            className={clsx(
              tabBase,
              "inline-flex items-center gap-1.5",
              tab.key === activeKey ? tabActive : tabIdle,
            )}
          >
            {tab.icon}
            {tab.label}
            {!!tab.badge && (
              <span
                className={clsx(
                  "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-bold",
                  tab.key === activeKey
                    ? "bg-white/25 text-white"
                    : "bg-[rgb(var(--text-accent)/0.14)] text-[rgb(var(--text-accent))]",
                )}
              >
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>
      <div
        id={`tab-panel-${active?.key}`}
        role="tabpanel"
        className={
          panelStyle === "card"
            ? "bg-[rgb(var(--card))] rounded-2xl border border-[rgb(var(--border))] p-5 shadow-lifted"
            : undefined
        }
      >
        {active?.content}
      </div>
    </div>
  );
}
